import React, { useState, useEffect, useMemo } from 'react';
import { Upload, Download, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toCanonical, normalizeUnitKey } from '../lib/units';
import { createRecipe } from '../lib/api';
import logger from '../lib/logger';
import { useDirtyExitGuard } from '../lib/useDirtyExitGuard';

/**
 * RecipeUploadDialog — bulk import recipes from CSV / XLSX.
 *
 * Pipeline:
 *   1. User uploads file → SheetJS parse (.csv | .xlsx).
 *   2. Show preview + column mapping (auto-detect with manual override).
 *   3. Validate every row (unit recognized, ingredient resolves, qty > 0, etc).
 *   4. Group rows by recipe_name → 1 recipe per name with N lines.
 *   5. Show summary: recipes_detected, rows_parsed, unresolved, invalid_units,
 *      duplicate_names_existing.
 *   6. Operator clicks "Import" → POST /api/inventory/recipes for each new recipe.
 *      Existing names are SKIPPED (not silently overwritten).
 *
 * Required columns (mappable):
 *   recipe_name | yield_quantity | yield_unit | quantity | unit
 *   one of: ingredient_id | ingredient_name
 * Optional:
 *   recipe_type | category | portion_size | portion_unit | wastage_percent | notes
 *
 * 1OS doctrine: Preps are first-class ingredients. A prep's `acts_as_ingredient_id`
 * is the canonical id stored on every recipe line — there is NO separate
 * "prep ingredient" identity. The resolver therefore searches both the
 * regular ingredient master AND the prep catalog (by prep_name), and when a
 * prep wins the match the line is stored with `prep.acts_as_ingredient_id`.
 *
 * Resolution priority (highest → lowest):
 *   1. exact ingredient_id match
 *   2. exact regular ingredient name match (case-insensitive trim)
 *   3. exact prep_name match → resolves to prep.acts_as_ingredient_id
 *   4. fuzzy regular ingredient match (alphanumeric-only key)
 *   5. fuzzy prep_name match → resolves to prep.acts_as_ingredient_id
 *   6. unresolved
 */

const TEMPLATE_HEADERS = [
  'recipe_name', 'recipe_type', 'category', 'portion_size', 'portion_unit',
  'yield_quantity', 'yield_unit',
  'ingredient_name', 'ingredient_id', 'quantity', 'unit',
  'wastage_percent', 'notes',
];
// Optional column: only present when the file came from an Export (round-trip).
// If absent, conflict detection is skipped — backward-compatible with hand-built
// uploads.
const OPTIONAL_HEADERS = ['last_updated_at'];
const TEMPLATE_SAMPLE = [
  ['Margherita Pizza', 'MENU_ITEM', 'Pizza', 1, 'plate', 1, 'portion', 'Pizza Dough',  '', 250, 'gm', 0, 'Stretch by hand'],
  ['Margherita Pizza', 'MENU_ITEM', 'Pizza', 1, 'plate', 1, 'portion', 'Tomato Sauce', '', 80,  'gm', 0, ''],
  ['Margherita Pizza', 'MENU_ITEM', 'Pizza', 1, 'plate', 1, 'portion', 'Mozzarella',   '', 100, 'gm', 5, ''],
  ['Pasta Alfredo',    'MENU_ITEM', 'Mains', 1, 'portion', 1, 'portion', 'White Sauce', '', 100, 'gm', 0, 'Prep used as ingredient'],
  ['Lemonade',         'MENU_ITEM', 'Beverage', 1, 'glass', 1, 'portion', 'Lemon',     '', 30,  'gm', 0, ''],
  ['Lemonade',         'MENU_ITEM', 'Beverage', 1, 'glass', 1, 'portion', 'Sugar',     '', 20,  'gm', 0, ''],
];

const REQUIRED_FIELDS = ['recipe_name', 'yield_quantity', 'yield_unit', 'quantity', 'unit'];
const RESOLVABLE = ['ingredient_id', 'ingredient_name'];

const guessMapping = (headers) => {
  const out = {};
  const norm = (s) => (s || '').toString().toLowerCase().replace(/[\s_-]/g, '');
  [...TEMPLATE_HEADERS, ...OPTIONAL_HEADERS].forEach((target) => {
    const t = norm(target);
    const found = headers.find((h) => norm(h) === t);
    if (found) out[target] = found;
  });
  return out;
};

const RecipeUploadDialog = ({
  open, onClose,
  ingredients = [], preps = [],
  existingRecipes = [],
  existingRecipeNames = [],
  outletId = null,
  onImported,
}) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'preview' | 'importing'
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Aggressive normalization for fuzzy matching: strip non-alphanumerics +
  // collapse whitespace + lowercase. So "White-Sauce", "white sauce",
  // "WHITE_SAUCE" all share the same fuzzy key.
  const _fuzzyKey = (s) =>
    (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

  const ingredientsByName = useMemo(() => {
    // Sorted alphabetical lookup map (case-insensitive) for resolution
    const m = new Map();
    [...ingredients]
      .sort((a, b) =>
        ((a.ingredient_name || a.name || '')).localeCompare(
          (b.ingredient_name || b.name || ''), undefined, { sensitivity: 'base' }
        )
      )
      .forEach((i) => {
        const n = (i.ingredient_name || i.name || '').trim().toLowerCase();
        if (n && !m.has(n)) m.set(n, i);
      });
    return m;
  }, [ingredients]);
  const ingredientsById = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => m.set(i.ingredient_id, i));
    return m;
  }, [ingredients]);
  const ingredientsByFuzzyKey = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => {
      const k = _fuzzyKey(i.ingredient_name || i.name);
      if (k && !m.has(k)) m.set(k, i);
    });
    return m;
  }, [ingredients]);

  // Prep lookups: by acts_as_ingredient_id, by exact prep_name, by fuzzy key.
  // A prep that resolves a recipe line is stored on the line as if it were
  // its `acts_as_ingredient_id` — never as the prep_id itself.
  const prepsByActsAsId = useMemo(() => {
    const m = new Map();
    (preps || []).forEach((p) => {
      const a = p.acts_as_ingredient_id;
      if (a && !m.has(a)) m.set(a, p);
    });
    return m;
  }, [preps]);
  const prepsByName = useMemo(() => {
    const m = new Map();
    (preps || []).forEach((p) => {
      const n = (p.prep_name || '').trim().toLowerCase();
      if (n && !m.has(n)) m.set(n, p);
    });
    return m;
  }, [preps]);
  const prepsByFuzzyKey = useMemo(() => {
    const m = new Map();
    (preps || []).forEach((p) => {
      const k = _fuzzyKey(p.prep_name);
      if (k && !m.has(k)) m.set(k, p);
    });
    return m;
  }, [preps]);

  const existingNamesLower = useMemo(() => new Set(
    (existingRecipeNames || []).map((s) => (s || '').trim().toLowerCase())
  ), [existingRecipeNames]);

  // Map recipe_name (lower) → updated_at ISO string. Used to detect when an
  // operator-edited export has gone stale: if the file's `last_updated_at`
  // doesn't match the current DB value, we surface STALE_ROW_CONFLICT.
  const existingUpdatedAtByName = useMemo(() => {
    const m = new Map();
    (existingRecipes || []).forEach((r) => {
      const n = (r.recipe_name || '').trim().toLowerCase();
      if (n) m.set(n, r.updated_at || r.created_at || '');
    });
    return m;
  }, [existingRecipes]);

  // Operator must explicitly acknowledge stale-row conflicts before importing.
  const [conflictsAcknowledged, setConflictsAcknowledged] = useState(false);
  useEffect(() => {
    // Reset acknowledgment whenever the file/mapping changes.
    setConflictsAcknowledged(false);
  }, [rawRows, mapping]);

  // Dirty when the user has done anything beyond opening the dialog:
  // advanced past upload step, parsed headers/rows, or set any mapping.
  // Once import is complete (importResults set), treat as non-dirty —
  // the user's work is already persisted.
  const isDirty = useMemo(() => {
    if (importResults) return false;
    if (step !== 'upload') return true;
    if (rawHeaders.length > 0 || rawRows.length > 0) return true;
    if (Object.keys(mapping).length > 0) return true;
    return false;
  }, [step, rawHeaders, rawRows, mapping, importResults]);

  const reset = () => {
    setStep('upload');
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    setImporting(false);
    setImportResults(null);
  };

  const closeAndReset = () => {
    reset();
    onClose?.();
  };

  const exitGuard = useDirtyExitGuard({
    isDirty,
    onConfirmClose: closeAndReset,
    title: 'Exit recipe upload?',
    body: 'You have unsaved recipe upload progress. If you exit now, your uploaded file and column mapping will be lost.',
    testIdPrefix: 'recipe-upload-exit-confirm',
  });

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      if (!json.length) {
        toast.error('File is empty');
        return;
      }
      const headers = Object.keys(json[0]);
      setRawHeaders(headers);
      setRawRows(json);
      setMapping(guessMapping(headers));
      setStep('mapping');
    } catch (e) {
      logger.error('upload parse failed', e);
      toast.error('Could not parse file. Please use CSV or XLSX format.');
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recipes');
    XLSX.writeFile(wb, 'recipe_import_template.xlsx');
  };

  const downloadCsvTemplate = () => {
    const csv = [TEMPLATE_HEADERS.join(','), ...TEMPLATE_SAMPLE.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recipe_import_template.csv';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  const _RECIPE_YIELD_PASSTHROUGH = ['portion', 'portions', 'serving', 'servings', 'plate', 'plates', 'glass', 'glasses'];
  const isYieldUnitOk = (u) => {
    const raw = (u || '').toString().trim().toLowerCase();
    if (_RECIPE_YIELD_PASSTHROUGH.includes(raw)) return true;
    return !toCanonical(1, u).conversion_failed;
  };

  // Validate + group rows once mapping is set
  const validation = useMemo(() => {
    if (!rawRows.length) return null;
    const get = (row, target) => {
      const src = mapping[target];
      return src ? row[src] : '';
    };

    const groups = new Map();
    const issues = [];
    const unresolvedNames = new Set();
    const invalidUnits = new Set();
    const dupExisting = new Set();
    const staleConflicts = new Set(); // recipe names where exported `last_updated_at` no longer matches DB

    rawRows.forEach((row, rIdx) => {
      const name = (get(row, 'recipe_name') || '').toString().trim();
      const recipeType = (get(row, 'recipe_type') || 'MENU_ITEM').toString().trim().toUpperCase();
      const category = (get(row, 'category') || '').toString().trim();
      const portionSize = get(row, 'portion_size');
      const portionUnit = (get(row, 'portion_unit') || '').toString().trim();
      const yieldRaw = get(row, 'yield_quantity');
      const yieldUnit = (get(row, 'yield_unit') || '').toString().trim();
      const ingId = (get(row, 'ingredient_id') || '').toString().trim();
      const ingName = (get(row, 'ingredient_name') || '').toString().trim();
      const qtyRaw = get(row, 'quantity');
      const unit = (get(row, 'unit') || '').toString().trim();
      const wastageRaw = get(row, 'wastage_percent');
      const notes = (get(row, 'notes') || '').toString();
      const fileLastUpdated = (get(row, 'last_updated_at') || '').toString().trim();

      if (!name) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'recipe_name missing' }); return; }
      if (!yieldRaw) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'yield_quantity missing' }); return; }
      if (!yieldUnit) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'yield_unit missing' }); return; }
      if (!qtyRaw) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'quantity missing' }); return; }
      if (!unit) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'unit missing' }); return; }

      const yield_qty = Number(yieldRaw);
      const qty = Number(qtyRaw);
      if (!isFinite(yield_qty) || yield_qty <= 0) { issues.push({ rowIndex: rIdx, type: 'invalid_qty', message: `bad yield_quantity: ${yieldRaw}` }); return; }
      if (!isFinite(qty) || qty <= 0) { issues.push({ rowIndex: rIdx, type: 'invalid_qty', message: `bad quantity: ${qtyRaw}` }); return; }

      if (!isYieldUnitOk(yieldUnit)) {
        invalidUnits.add(yieldUnit);
        issues.push({ rowIndex: rIdx, type: 'invalid_unit', message: `yield_unit '${yieldUnit}' not recognized` });
        return;
      }
      if (toCanonical(1, unit).conversion_failed) {
        invalidUnits.add(unit);
        issues.push({ rowIndex: rIdx, type: 'invalid_unit', message: `unit '${unit}' not recognized` });
        return;
      }

      let resolved = null;
      let resolvedFromPrep = null; // when truthy, holds the matched prep doc

      // Priority 1: exact ingredient_id (regular OR a prep's acts_as_ingredient_id)
      if (ingId) {
        resolved = ingredientsById.get(ingId);
        if (!resolved) {
          const prepHit = prepsByActsAsId.get(ingId);
          if (prepHit) {
            resolvedFromPrep = prepHit;
            resolved = {
              ingredient_id: prepHit.acts_as_ingredient_id,
              ingredient_name: prepHit.prep_name,
            };
          }
        }
      }
      // Priority 2: exact regular ingredient name
      if (!resolved && ingName) {
        resolved = ingredientsByName.get(ingName.toLowerCase());
      }
      // Priority 3: exact prep name → resolves to prep.acts_as_ingredient_id
      if (!resolved && ingName) {
        const prepHit = prepsByName.get(ingName.toLowerCase());
        if (prepHit && prepHit.acts_as_ingredient_id) {
          resolvedFromPrep = prepHit;
          resolved = {
            ingredient_id: prepHit.acts_as_ingredient_id,
            ingredient_name: prepHit.prep_name,
          };
        }
      }
      // Priority 4: fuzzy regular ingredient
      if (!resolved && ingName) {
        const fk = _fuzzyKey(ingName);
        if (fk) resolved = ingredientsByFuzzyKey.get(fk);
      }
      // Priority 5: fuzzy prep name → acts_as_ingredient_id
      if (!resolved && ingName) {
        const fk = _fuzzyKey(ingName);
        if (fk) {
          const prepHit = prepsByFuzzyKey.get(fk);
          if (prepHit && prepHit.acts_as_ingredient_id) {
            resolvedFromPrep = prepHit;
            resolved = {
              ingredient_id: prepHit.acts_as_ingredient_id,
              ingredient_name: prepHit.prep_name,
            };
          }
        }
      }

      if (!resolved) {
        unresolvedNames.add(ingName || `(row ${rIdx + 1})`);
        issues.push({ rowIndex: rIdx, type: 'unresolved', message: `cannot resolve "${ingName || ingId}"` });
        return;
      }

      // Skip-existing detection
      if (existingNamesLower.has(name.toLowerCase())) {
        dupExisting.add(name);
        // Stale-row guard: only triggers when the export-time timestamp is
        // present in the file (round-trip from Export). Backward-compatible:
        // hand-built uploads without `last_updated_at` skip this check.
        if (fileLastUpdated) {
          const dbStamp = existingUpdatedAtByName.get(name.toLowerCase()) || '';
          if (dbStamp && dbStamp !== fileLastUpdated) {
            staleConflicts.add(name);
          }
        }
      }

      if (!groups.has(name)) {
        groups.set(name, {
          recipe_name: name,
          recipe_type: ['MENU_ITEM', 'PREP'].includes(recipeType) ? recipeType : 'MENU_ITEM',
          category: category || null,
          portion_size: portionSize ? Number(portionSize) : null,
          portion_unit: portionUnit || null,
          yield_qty,
          yield_unit: yieldUnit,
          notes: notes || '',
          lines: [],
          file_last_updated_at: fileLastUpdated || null,
        });
      } else {
        const g = groups.get(name);
        if (g.yield_qty !== yield_qty || g.yield_unit !== yieldUnit) {
          issues.push({ rowIndex: rIdx, type: 'inconsistent', message: `recipe "${name}" has conflicting yield_quantity or yield_unit across rows` });
          return;
        }
      }
      groups.get(name).lines.push({
        ingredient_id: resolved.ingredient_id,
        ingredient_name: resolved.ingredient_name || resolved.name,
        is_prep: !!resolvedFromPrep,
        prep_id: resolvedFromPrep ? resolvedFromPrep.prep_id : null,
        qty,
        base_unit: unit,
        wastage_percent: wastageRaw ? Number(wastageRaw) : 0,
        notes,
      });
    });

    return {
      recipes: Array.from(groups.values()),
      issues,
      unresolvedNames: Array.from(unresolvedNames),
      invalidUnits: Array.from(invalidUnits),
      dupExisting: Array.from(dupExisting),
      staleConflicts: Array.from(staleConflicts),
    };
  }, [rawRows, mapping, ingredientsByName, ingredientsById, ingredientsByFuzzyKey,
      prepsByActsAsId, prepsByName, prepsByFuzzyKey, existingNamesLower,
      existingUpdatedAtByName]);

  const importable = validation
    ? validation.recipes.filter((r) => !existingNamesLower.has(r.recipe_name.toLowerCase()))
    : [];
  const hasUnacknowledgedStale =
    !!validation && validation.staleConflicts.length > 0 && !conflictsAcknowledged;
  const canImport =
    validation && importable.length > 0 && !hasUnacknowledgedStale;

  const runImport = async () => {
    if (!validation || !importable.length) return;
    setImporting(true);
    setStep('importing');
    const out = { created: [], failed: [], skippedDupes: validation.dupExisting };
    for (const r of importable) {
      try {
        await createRecipe({
          recipe_name: r.recipe_name,
          recipe_type: r.recipe_type,
          category: r.category,
          portion_size: r.portion_size,
          portion_unit: r.portion_unit,
          yields: { yield_qty: r.yield_qty, yield_unit: r.yield_unit },
          outlet_id: outletId || null,
          notes: r.notes,
          lines: r.lines.map((l) => ({
            line_type: 'INGREDIENT',
            ingredient_id: l.ingredient_id,
            qty: l.qty,
            base_unit: normalizeUnitKey(l.base_unit),
            wastage_percent: l.wastage_percent || 0,
            notes: l.notes || '',
          })),
        });
        out.created.push(r.recipe_name);
      } catch (e) {
        out.failed.push({ name: r.recipe_name, error: e?.response?.data?.detail || e.message });
        logger.error('recipe import failed', r.recipe_name, e);
      }
    }
    setImporting(false);
    setImportResults(out);
    if (out.failed.length === 0) {
      toast.success(`Imported ${out.created.length} recipe(s)`);
      onImported?.();
    } else {
      toast.warning(`Imported ${out.created.length}, failed ${out.failed.length}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) exitGuard.requestClose(); }}>
      <DialogContent
        className="bg-[#18181b] border-[#27272a] text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="recipe-upload-dialog"
        onEscapeKeyDown={exitGuard.onEscapeKeyDown}
        onPointerDownOutside={exitGuard.onPointerDownOutside}
        onInteractOutside={exitGuard.onInteractOutside}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Bulk Import Recipes</DialogTitle>
          <DialogDescription className="sr-only">
            Upload a CSV or XLSX file with recipe rows, map your columns to expected
            fields, then preview and import. Existing recipe names are skipped.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">Upload a CSV or XLSX file. Multiple rows with the same <code>recipe_name</code> are grouped into one recipe with multiple lines.</p>
            <p className="text-xs text-zinc-500" data-testid="recipe-upload-prep-hint">
              <strong className="text-zinc-300">Preps are first-class.</strong> Use a prep&apos;s name in the <code>ingredient_name</code> column (e.g. <code>White Sauce</code>) and it&apos;ll resolve to the prep automatically — no separate ingredient needed.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm"
                data-testid="recipe-download-xlsx-template-btn"
              ><Download className="w-4 h-4" /> Download .xlsx template</button>
              <button
                onClick={downloadCsvTemplate}
                className="flex items-center gap-2 px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm"
                data-testid="recipe-download-csv-template-btn"
              ><Download className="w-4 h-4" /> Download .csv template</button>
            </div>
            <label className="block border-2 border-dashed border-[#3f3f46] rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/50 transition-colors" data-testid="recipe-upload-dropzone">
              <Upload className="w-10 h-10 mx-auto mb-2 text-zinc-500" />
              <p className="text-sm text-zinc-300">Click to choose file or drop here</p>
              <p className="text-xs text-zinc-500 mt-1">Accepts .csv, .xlsx, .xls</p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="recipe-upload-file-input"
              />
            </label>
          </div>
        )}

        {step === 'mapping' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">Map your columns to expected fields. Auto-detected mappings shown.</p>
            <div className="grid grid-cols-2 gap-3" data-testid="recipe-mapping-grid">
              {TEMPLATE_HEADERS.map((target) => {
                const required = REQUIRED_FIELDS.includes(target);
                const ok = !!mapping[target];
                return (
                  <div key={target}>
                    <label className="block text-xs text-zinc-400 mb-1">
                      {target}{required && <span className="text-red-400 ml-1">*</span>}
                    </label>
                    <select
                      value={mapping[target] || ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [target]: e.target.value }))}
                      className={`w-full px-2 py-2 bg-[#09090b] border rounded-md text-sm ${ok ? 'border-emerald-500/40' : 'border-[#3f3f46]'}`}
                      data-testid={`recipe-mapping-select-${target}`}
                    >
                      <option value="">— skip —</option>
                      {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500">
              At least one of <code>ingredient_id</code> or <code>ingredient_name</code> is required to resolve ingredients.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep('upload')} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm" data-testid="recipe-mapping-back-btn">Back</button>
              <button
                onClick={() => setStep('preview')}
                disabled={!REQUIRED_FIELDS.every((f) => mapping[f]) || !RESOLVABLE.some((f) => mapping[f])}
                className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold disabled:opacity-40"
                data-testid="recipe-mapping-next-btn"
              >Continue →</button>
            </div>
          </div>
        )}

        {step === 'preview' && validation && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center" data-testid="recipe-preview-summary">
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Recipes detected</p>
                <p className="text-xl font-mono font-bold text-emerald-500" data-testid="recipe-preview-count">{validation.recipes.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Rows parsed</p>
                <p className="text-xl font-mono font-bold">{rawRows.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Unresolved</p>
                <p className={`text-xl font-mono font-bold ${validation.unresolvedNames.length ? 'text-yellow-400' : 'text-zinc-300'}`} data-testid="recipe-preview-unresolved-count">{validation.unresolvedNames.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Invalid units</p>
                <p className={`text-xl font-mono font-bold ${validation.invalidUnits.length ? 'text-yellow-400' : 'text-zinc-300'}`}>{validation.invalidUnits.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Duplicates (skipped)</p>
                <p className={`text-xl font-mono font-bold ${validation.dupExisting.length ? 'text-amber-400' : 'text-zinc-300'}`} data-testid="recipe-preview-dup-count">{validation.dupExisting.length}</p>
              </div>
              {validation.staleConflicts.length > 0 && (
                <div className="p-2 bg-amber-500/5 border border-amber-500/40 rounded">
                  <p className="text-xs text-amber-300">Stale conflicts</p>
                  <p
                    className="text-xl font-mono font-bold text-amber-400"
                    data-testid="recipe-preview-stale-count"
                  >
                    {validation.staleConflicts.length}
                  </p>
                </div>
              )}
            </div>

            {validation.issues.length > 0 && (
              <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3" data-testid="recipe-preview-issues">
                <p className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {validation.issues.length} issue(s) — these rows will NOT be imported
                </p>
                <ul className="text-xs text-zinc-300 max-h-40 overflow-auto space-y-1">
                  {validation.issues.slice(0, 30).map((i, idx) => (
                    <li key={idx}>Row {i.rowIndex + 2}: <span className="text-red-300">{i.type}</span> — {i.message}</li>
                  ))}
                  {validation.issues.length > 30 && <li className="text-zinc-500">… and {validation.issues.length - 30} more</li>}
                </ul>
              </div>
            )}

            {validation.staleConflicts.length > 0 && (
              <div
                className="bg-amber-500/5 border border-amber-500/30 rounded-md p-3 space-y-2"
                data-testid="recipe-preview-stale-callout"
              >
                <p className="text-sm font-bold text-amber-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {validation.staleConflicts.length} recipe(s) have been modified since you exported them
                </p>
                <p className="text-xs text-zinc-300">
                  {validation.staleConflicts.join(', ')}
                </p>
                <p className="text-xs text-amber-300/80">
                  These rows will be <strong>skipped</strong> by default to prevent overwriting newer data. Tick the box below to acknowledge and proceed with the rest of the file.
                </p>
                <label className="flex items-center gap-2 text-xs text-amber-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={conflictsAcknowledged}
                    onChange={(e) => setConflictsAcknowledged(e.target.checked)}
                    className="cursor-pointer accent-amber-500"
                    data-testid="recipe-preview-stale-ack"
                  />
                  I&apos;ve reviewed the stale-row conflicts and want to proceed.
                </label>
              </div>
            )}

            {validation.dupExisting.length > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-md p-3" data-testid="recipe-preview-dup-list">
                <p className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {validation.dupExisting.length} recipe name(s) already exist — they will be skipped (not overwritten)
                </p>
                <p className="text-xs text-zinc-300">{validation.dupExisting.join(', ')}</p>
              </div>
            )}

            <div className="bg-[#09090b] border border-[#27272a] rounded-md max-h-72 overflow-auto" data-testid="recipe-preview-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#18181b]">
                  <tr className="text-left text-zinc-400">
                    <th className="px-2 py-2">Recipe</th><th className="px-2 py-2">Type</th>
                    <th className="px-2 py-2">Yield</th><th className="px-2 py-2">Lines</th>
                    <th className="px-2 py-2">Ingredients (resolved)</th>
                  </tr>
                </thead>
                <tbody>
                  {validation.recipes.slice(0, 50).map((r, idx) => {
                    const isDup = existingNamesLower.has(r.recipe_name.toLowerCase());
                    const isStale = validation.staleConflicts.includes(r.recipe_name);
                    return (
                      <tr key={idx} className="border-t border-[#27272a]">
                        <td className="px-2 py-2 font-medium" data-testid={`recipe-preview-row-${idx}`}>
                          {r.recipe_name}
                          {isDup && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-500/20 text-amber-400 rounded">SKIP</span>}
                          {isStale && (
                            <span
                              className="ml-1 text-[10px] px-1 py-0.5 bg-amber-500/30 text-amber-300 rounded uppercase tracking-wider"
                              data-testid={`recipe-preview-stale-chip-${idx}`}
                              title="Modified since export"
                            >
                              STALE
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 font-mono text-zinc-400">{r.recipe_type}</td>
                        <td className="px-2 py-2 font-mono">{r.yield_qty} {r.yield_unit}</td>
                        <td className="px-2 py-2 font-mono">{r.lines.length}</td>
                        <td className="px-2 py-2 text-zinc-400 truncate max-w-xs">
                          {r.lines.map((l, li) => (
                            <span key={li}>
                              {li > 0 && <span className="text-zinc-600"> · </span>}
                              <span data-testid={`recipe-preview-line-${idx}-${li}`}>
                                {l.ingredient_name}
                                {l.is_prep && (
                                  <span
                                    className="ml-1 text-[9px] px-1 py-0.5 bg-purple-500/20 text-purple-300 rounded uppercase tracking-wider"
                                    data-testid={`recipe-preview-prep-badge-${idx}-${li}`}
                                  >
                                    Prep
                                  </span>
                                )}
                                <span className="text-zinc-500"> {l.qty}{l.base_unit}</span>
                              </span>
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep('mapping')} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm">Back</button>
              <button
                onClick={runImport}
                disabled={!canImport || importing}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold disabled:opacity-40"
                data-testid="recipe-preview-import-btn"
                title={
                  hasUnacknowledgedStale
                    ? 'Acknowledge stale-row conflicts to proceed'
                    : (validation.dupExisting.length ? 'Existing recipes will be skipped' : '')
                }
              >
                Import {importable.length} recipe(s)
                {validation.dupExisting.length > 0 ? ` · skip ${validation.dupExisting.length}` : ''}
                {validation.issues.length > 0 ? ` · ${validation.issues.length} bad rows` : ''}
              </button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="space-y-4 py-4" data-testid="recipe-import-progress">
            {importing ? (
              <p className="text-zinc-400">Importing… please wait.</p>
            ) : importResults && (
              <div className="space-y-2">
                <p className="text-emerald-400 font-bold">Created {importResults.created.length} recipe(s)</p>
                {importResults.skippedDupes?.length > 0 && (
                  <p className="text-amber-400 text-sm">Skipped {importResults.skippedDupes.length} existing: {importResults.skippedDupes.join(', ')}</p>
                )}
                {importResults.failed.length > 0 && (
                  <>
                    <p className="text-red-400 font-bold">Failed {importResults.failed.length}:</p>
                    <ul className="text-xs text-zinc-300 space-y-1">
                      {importResults.failed.map((f, i) => <li key={i}>• {f.name} — {typeof f.error === 'string' ? f.error : JSON.stringify(f.error)}</li>)}
                    </ul>
                  </>
                )}
                <div className="flex justify-end pt-2">
                  <button onClick={closeAndReset} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold">Close</button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {exitGuard.ConfirmDialog}
    </Dialog>
  );
};

export default RecipeUploadDialog;
